import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer den Mitarbeiterverfügbarkeitskalender
 * (`EmployeeAvailabilityTimelineComponent` — FullCalendar-Resource-Timeline mit
 * einer Zeile pro Mitarbeiter und Auslastungs-Ring). Wird ueber einen
 * `?`-Button in der Kalender-Toolbar gestartet (Pattern analog
 * `STAFFING_BEDARFSPLAN_HELP_TOUR`).
 *
 * Vier Schritte — bewusst dicht, damit der User nicht abbricht:
 *  1. Ueberblick: wofuer der Kalender da ist
 *  2. Mitarbeiter-Zeile mit Auslastungs-Ring (% verfuegbar vs. Vertragsstunden)
 *  3. Verfuegbarkeits-Bloecke im Grid + Tag/Woche-Umschalter + Klick zum Anlegen
 *  4. Warum es zaehlt: Verfuegbarkeit ist die Grundlage der Schichtplanung
 *
 * Trigger ausschliesslich manuell. State-Policy erlaubt beliebiges Replay
 * (Hilfe-Modus, kein Setup-Schritt).
 */
export const AVAILABILITY_CALENDAR_HELP_TOUR: TourConfig = {
  id: TourFeature.AvailabilityCalendarHelp,
  version: 1,
  title: 'AVAILABILITY_CALENDAR_HELP_TOUR_TITLE',
  description: 'AVAILABILITY_CALENDAR_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  triggerContext: {
    route: '/availability',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 70,
  // Klassische Guided Tour (Spotlight + Voice-Over), nicht Help-Mode-Overlay.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'AVAILABILITY_CALENDAR_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          // Spotlight auf den ganzen Kalender-Container.
          anchor: AnchorId.AvailabilityCalendarView,
          spotlightAnchor: AnchorId.AvailabilityCalendarView,
          title: 'TUTORIAL.AVAILABILITY_CALENDAR.WELCOME.TITLE',
          content: 'TUTORIAL.AVAILABILITY_CALENDAR.WELCOME.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'employee-row',
          // Erste Mitarbeiter-Kachel (Auslastungs-Ring). Anchor wird per
          // resourceLabelContent in den FC-HTML-String injiziert; fehlt er
          // (keine Verfuegbarkeitsdaten), wird der Step uebersprungen.
          anchor: AnchorId.AvailabilityEmployeeRow,
          spotlightAnchor: AnchorId.AvailabilityEmployeeRow,
          title: 'TUTORIAL.AVAILABILITY_CALENDAR.EMPLOYEE_ROW.TITLE',
          content: 'TUTORIAL.AVAILABILITY_CALENDAR.EMPLOYEE_ROW.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="availability-employee-row"]'),
        },
        {
          id: 'availability-blocks',
          // Kalender-Grid mit den farbigen Verfuegbarkeits-Bloecken.
          anchor: AnchorId.AvailabilityCalendarGrid,
          spotlightAnchor: AnchorId.AvailabilityCalendarGrid,
          title: 'TUTORIAL.AVAILABILITY_CALENDAR.BLOCKS.TITLE',
          content: 'TUTORIAL.AVAILABILITY_CALENDAR.BLOCKS.CONTENT',
          placement: 'top',
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="availability-calendar-grid"]'),
        },
        {
          id: 'shift-planning',
          // Zurueck auf den ganzen Kalender — das "Warum".
          anchor: AnchorId.AvailabilityCalendarView,
          spotlightAnchor: AnchorId.AvailabilityCalendarView,
          title: 'TUTORIAL.AVAILABILITY_CALENDAR.SHIFT_PLANNING.TITLE',
          content: 'TUTORIAL.AVAILABILITY_CALENDAR.SHIFT_PLANNING.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
        },
      ],
    },
  ],
};
